import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { AuthURL } from "../../routes/auth_routes/AuthURL";
import BaseIcon from "../svg_icons/BaseIcon";
import { sidebarItems } from "./components/ca_sidebar_items";
import SidebarFloatingChildren from "./components/SidebarFloatingChildren";
import "../../css/components/page_wrappers/CAPageWrapper.css";

const Sidebar = ({ isPinnedOpen, setIsPinnedOpen, isHovered, setIsHovered, hoveredMenu, setHoveredMenu }) => {
    const brandName = process.env.REACT_APP_BRAND_NAME;

    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    const isSidebarOpen = isPinnedOpen || isHovered;

    /* =========================================================
    GROUP REFS
    ========================================================= */
    const groupRefs = useRef({});
    const sidebarRef = useRef(null);

    /* =========================================================
    HOVER CLOSE TIMER
    ========================================================= */
    const hoverCloseTimer = useRef(null);

    /* =========================================================
    PATH ACTIVE
    ========================================================= */
    const isPathActive = (path) => {
        return (
            location.pathname === path || location.pathname.startsWith(`${path}/`)
        );
    };

    /* =========================================================
    PARENT ACTIVE
    ========================================================= */
    const isParentActive = (item) => {
        if (!item.children) { return isPathActive(item.path); }

        return item.children.some((child) => {
            const activeByRoutes = child.activeRoutes?.some(
                (route) =>
                location.pathname === route ||
                location.pathname.startsWith(`${route}/`),
            );

            return activeByRoutes || isPathActive(child.path);
        });
    };

    /* =========================================================
    GROUP REF
    ========================================================= */
    const getGroupRef = (title) => {
        if (!groupRefs.current[title]) {
            groupRefs.current[title] = React.createRef();
        }
        return groupRefs.current[title];
    };

    /* =========================================================
    OPEN / CLOSE FLOATING MENU
    ========================================================= */
    const openFloatingMenu = (title) => {
        if (hoverCloseTimer.current) {
            clearTimeout(hoverCloseTimer.current);
            hoverCloseTimer.current = null;
        }
        setHoveredMenu(title);
    };

    const closeFloatingMenu = () => {
        hoverCloseTimer.current = setTimeout(() => {
            setHoveredMenu(null);
            hoverCloseTimer.current = null;
        }, 180);
    };

    useEffect(() => {
        return () => {
            if (hoverCloseTimer.current) {
                clearTimeout(hoverCloseTimer.current);
            }
        };
    }, []);

    // Close menu on route change
    useEffect(() => { 
        setHoveredMenu(null); 
    }, [location.pathname, setHoveredMenu]);

    return (
        <aside
            ref          = { sidebarRef }
            className    = { `ca-sidebar ${
                isSidebarOpen ? "ca-sidebar-open" : "ca-sidebar-closed"
            } ${isPinnedOpen ? "ca-sidebar-pinned" : ""}`}
            onMouseEnter = { () => {
                if (!isPinnedOpen) { setIsHovered(true); }
            }}
            onMouseLeave = { () => {
                if (!isPinnedOpen) { setIsHovered(false); }
                closeFloatingMenu();
            }}
        >
            {/* =================================================
                SIDEBAR HEADER
            ================================================= */}
            <div className="ca-sidebar-header">
                <div className="ca-sidebar-brand">
                    <h2>{ brandName }</h2>
                </div>

                <button
                    type="button"
                    className="ca-sidebar-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsPinnedOpen((prev) => !prev);
                        setHoveredMenu(null);
                    }}
                    title={isPinnedOpen ? "Collapse sidebar" : "Pin sidebar"}
                >
                    {isPinnedOpen ? "✖" : "☰"}
                </button>
            </div>

            {/* =================================================
                SIDEBAR ITEMS
            ================================================= */}
            <div className="ca-sidebar-items">
                {sidebarItems.map((item) => {
                    const hasChildren = Boolean(item.children);

                    if (!hasChildren) {
                        const isActive = isPathActive(item.path);

                        return (
                            <div
                                key={item.title}
                                className={`ca-sidebar-item ${isActive ? "active" : ""}`}
                                onClick={() => {
                                    setHoveredMenu(null);
                                    navigate(item.path);
                                }}
                            >
                                <div className="ca-sidebar-item-content">
                                    <div className="ca-sidebar-item-icon">
                                        <BaseIcon name={item.icon} />
                                    </div>
                                    <span className="ca-sidebar-item-title">{item.title}</span>
                                </div>
                            </div>
                        );
                    }

                    const parentActive = isParentActive(item);
                    const groupRef = getGroupRef(item.title);
                    const isHoveredMenu = hoveredMenu === item.title;

                    return (
                        <div
                            key={item.title}
                            ref={groupRef}
                            className={`ca-sidebar-group ${isHoveredMenu ? "hovered" : ""}`}
                            onMouseEnter={() => openFloatingMenu(item.title)}
                            onMouseLeave={() => closeFloatingMenu()}
                        >
                            <div
                                className={`ca-sidebar-item ${parentActive ? "active" : ""}`}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="ca-sidebar-item-content">
                                    <div className="ca-sidebar-item-icon">
                                        <BaseIcon name={item.icon} />
                                    </div>
                                    <span className="ca-sidebar-item-title">{item.title}</span>
                                </div>

                                <span
                                    className={`ca-sidebar-chevron ${
                                        isHoveredMenu ? "expanded" : "collapsed"
                                    }`}
                                >
                                    {isHoveredMenu ? "▾" : "▸"}
                                </span>
                            </div>

                            <SidebarFloatingChildren
                                parentRef   = { groupRef }
                                sidebarRef  = { sidebarRef }
                                isOpen      = { isHoveredMenu }
                                children    = { item.children }
                                activeChild = { (child) => {
                                    const activeByRoutes = child.activeRoutes?.some(
                                        (route) =>
                                        location.pathname === route ||
                                        location.pathname.startsWith(`${route}/`),
                                    );
                                    return activeByRoutes || isPathActive(child.path);
                                }}
                                onMouseEnter = { () => openFloatingMenu(item.title) }
                                onMouseLeave = { () => closeFloatingMenu() }
                                onChildClick = { (child) => {
                                    setHoveredMenu(null);
                                    if (!isPinnedOpen) { setIsHovered(false); }
                                    navigate(child.path);
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {/* =================================================
                LOGOUT
            ================================================= */}
            <div className="ca-sidebar-footer">
                <div
                    title="Logout"
                    className="ca-sidebar-logout"
                    onClick={() => {
                        setHoveredMenu(null);
                        logout();
                    }}
                >
                    <div className="ca-sidebar-logout-icon">
                        <BaseIcon name="logOut" />
                    </div>
                    <span className="ca-sidebar-logout-title">Logout</span>
                </div>
            </div>
        </aside>
    );
};

const CAPageWrapper = ({ children }) => {
    const { isAuthenticated } = useAuth();

    const [isPinnedOpen, setIsPinnedOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredMenu, setHoveredMenu] = useState(null);

    if (!isAuthenticated) {
        return <Navigate to={AuthURL.LOGIN} replace />;
    }

    // Overlay backdrop active when hovering closed sidebar OR when hovering submenus
    const isOverlayActive = (!isPinnedOpen && isHovered) || hoveredMenu !== null;

    return (
        <div className={`ca-page-wrapper ${isPinnedOpen ? "ca-sidebar-pinned-active" : ""}`}>
            <Sidebar 
                isPinnedOpen={isPinnedOpen}
                setIsPinnedOpen={setIsPinnedOpen}
                isHovered={isHovered}
                setIsHovered={setIsHovered}
                hoveredMenu={hoveredMenu}
                setHoveredMenu={setHoveredMenu}
            />

            {/* Backdrop for dimming background on sidebar hover / dropdown open */}
            <div 
                className={`ca-sidebar-backdrop ${isOverlayActive ? "active" : ""}`}
                onClick={() => setHoveredMenu(null)}
            />

            {/* Right side content */}
            <main className="ca-page-content">
                <div className="ca-page-card">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default CAPageWrapper;
